// src/mocks/handlers/account/businessDetailsUpdateHandler.ts

import { rest } from 'msw';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';
import initialBusinessDetailsData from '@/mocks/data/businessDetails.json';

const businessDetailsData = JSON.parse(
  JSON.stringify(initialBusinessDetailsData),
);

const VALID_COUNTRY_CODES = [
  'AE',
  'AU',
  'BR',
  'CA',
  'CN',
  'DE',
  'DK',
  'EE',
  'GB',
  'HK',
  'ID',
  'IL',
  'JP',
  'KR',
  'MX',
  'NL',
  'NZ',
  'PL',
  'SG',
  'US',
];

export const businessDetailsUpdateHandler = rest.patch(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.ACCOUNT_BUSINESS_DETAILS}`,
  async (req, res, ctx) => {
    const { customer_uuid } = req.params;

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const businessDetails = businessDetailsData.find(
      (entry: any) => entry.uuid === customer_uuid,
    );

    const originalBusinessDetails = initialBusinessDetailsData.find(
      (entry: any) => entry.uuid === customer_uuid,
    );

    if (!businessDetails || !originalBusinessDetails) {
      return res(
        ctx.status(404),
        ctx.json({ error: 'Business details not found' }),
      );
    }

    const payload = await req.json();

    const validationErrors: any[] = [];

    Object.keys(payload).forEach((key) => {
      const originalValue =
        originalBusinessDetails[key as keyof typeof originalBusinessDetails];
      const newValue = payload[key];

      if (originalValue?.trim() && (!newValue || !newValue.trim())) {
        validationErrors.push({
          loc: ['body', key],
          msg: `${key.replace('_', ' ')} is required`,
          type: 'value_error',
        });
      }
    });

    if (
      'country_code' in payload &&
      payload.country_code &&
      !VALID_COUNTRY_CODES.includes(payload.country_code)
    ) {
      validationErrors.push({
        loc: ['body', 'country_code'],
        msg: 'Invalid country code',
        type: 'value_error',
      });
    }

    if (validationErrors.length > 0) {
      return res(
        ctx.status(422),
        ctx.json({
          detail: validationErrors,
          fullMessage: validationErrors.map((e) => e.msg).join(', '),
        }),
      );
    }

    const updatedBusinessDetails = {
      ...businessDetails,
      ...payload,
    };

    const index = businessDetailsData.findIndex(
      (entry: any) => entry.uuid === customer_uuid,
    );
    if (index !== -1) {
      businessDetailsData[index] = updatedBusinessDetails;
    }

    return res(ctx.status(200), ctx.json(updatedBusinessDetails));
  },
);
